import { SkillPayloadReferenceSchema } from "./skill-payload";
import { canonicalJsonStringify } from "./canonical-json";
import { z } from "zod";

const segment = /^[A-Za-z0-9_][A-Za-z0-9._-]*$/;
const filePath = z
  .string()
  .refine(
    (path) => path.split("/").every((part) => segment.test(part)) && path !== "SKILL.md",
    "Use a relative file path without traversal segments.",
  );

export const GlobalSkillCatalogSchema = z
  .object({
    schema_version: z.literal(1),
    legacy_helpers: z.record(z.string(), z.unknown()).optional(),
    skills: z
      .array(
        z
          .object({
            name: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
            description: z.string().min(1),
            content: z.string().min(1),
            files: z.record(filePath, z.string()),
          })
          .strict(),
      )
      .min(1)
      .max(10000),
  })
  .strict()
  .superRefine((catalog, ctx) => {
    if (new Set(catalog.skills.map((skill) => skill.name)).size !== catalog.skills.length) {
      ctx.addIssue({ code: "custom", message: "Skill names must be unique." });
    }
  });

export type GlobalSkillCatalog = z.infer<typeof GlobalSkillCatalogSchema>;

export function serializeGlobalSkillCatalog(input: GlobalSkillCatalog): string {
  const catalog = GlobalSkillCatalogSchema.parse(input);
  return JSON.stringify({
    schema_version: catalog.schema_version,
    ...(catalog.legacy_helpers ? { legacy_helpers: catalog.legacy_helpers } : {}),
    skills: [...catalog.skills]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((skill) => ({
        name: skill.name,
        description: skill.description,
        content: skill.content,
        files: Object.fromEntries(
          Object.entries(skill.files).sort(([a], [b]) => a.localeCompare(b)),
        ),
      })),
  });
}

export const GlobalSkillReleaseSchema = z
  .object({
    schema_version: z.literal(2),
    skills: z.array(SkillPayloadReferenceSchema),
  })
  .strict();
export type GlobalSkillRelease = z.infer<typeof GlobalSkillReleaseSchema>;
export function serializeGlobalSkillRelease(input: GlobalSkillRelease): string {
  const release = GlobalSkillReleaseSchema.parse(input);
  return canonicalJsonStringify({
    ...release,
    skills: [...release.skills].sort((a, b) => a.name.localeCompare(b.name)),
  });
}
